// The SDX web components (sdx-select, sdx-input, checkbox/radio inputs inside
// them, …) bake Swisscom's old brand blue directly into their shadow-DOM
// styling — no CSS custom property or ::part() hook exposes it, so it can't
// be themed the normal way (verified against the v2.155.0 bundle):
//  - focus rings, checked state and the selected-option row hardcode
//    #0445c8 / #0036a7 in their shadow stylesheet (light theme).
//  - sdx-select's closed value box is a nested, read-only sdx-input whose
//    border/text color it sets via an *inline* style (#0445c8 / #1781e3)
//    from JS whenever the dropdown is open — a stylesheet rule can only
//    override that with !important, and since it can't see "is this select
//    currently open", making it unconditional recolors the box even when
//    closed (regression: reported as "the whole dropdown is blue again").
//    So that one is handled reactively in JS instead, below.
const INTERACTION = "#026F6D";
const BRAND = "#004A53";
const PRIMARY = "#005654";

const OVERRIDE_CSS = `
:host(.focus-visible:not(.disabled):not(.inside-dialog)) { outline-color: ${INTERACTION} !important; }
:host(:focus-visible) { outline-color: ${INTERACTION} !important; }
.button-reset:focus-visible { outline-color: ${INTERACTION} !important; }
:host(.selected:not(.multiple):not(.inside-dialog)) { background-color: ${INTERACTION} !important; }
:host(.selected:not(.multiple):not(.inside-dialog):hover),
:host(.selected.focus:not(.multiple):not(.inside-dialog)) { background-color: ${BRAND} !important; }
.input.checkbox input:not(:checked).focus-visible+label::before,
.input.radio input:not(:checked).focus-visible+label::before { outline-color: ${INTERACTION} !important; }
.input.checkbox input:not(:checked):not(:disabled).focus:not(.focus-visible)+label::before,
.input.radio input:not(:checked):not(:disabled).focus:not(.focus-visible)+label::before { border-color: ${INTERACTION} !important; }
.input.checkbox input:checked+label,
.input.radio input:checked+label { --input-checked-color: ${INTERACTION} !important; }
.wrapper .input:focus { border-color: ${INTERACTION} !important; }
.wrapper .input:focus:focus-visible { outline-color: ${INTERACTION} !important; }
.wrapper .input { caret-color: ${INTERACTION} !important; }
.badge { background: ${INTERACTION} !important; }
.wrapper .input:hover:not(:focus) .badge { background-color: ${BRAND} !important; }

/* Dropdown chevron: drawn as two rotated ::before/::after bars, not an SVG —
   guarded to enabled+valid only so disabled (grey) and invalid (red) chevron
   states, which use the same properties, are left untouched. */
.component:not(.disabled):not(.invalid) .header .thumb>.icon::before,
.component:not(.disabled):not(.invalid) .header .thumb>.icon::after { background: ${INTERACTION} !important; }
.component:not(.disabled):not(.invalid) .header:hover .thumb>.icon::before,
.component:not(.disabled):not(.invalid) .header:hover .thumb>.icon::after { background: ${BRAND} !important; }

/* sdx-expand-and-collapse ("Passwortkriterien" toggle) renders its clickable
   row as a separate nested sdx-accordion-item-header (its own component,
   own shadow root — the arrow lives here, not in expand-and-collapse's own
   shadow tree, which only holds unused leftover CSS for it). Label text
   itself is already #333 (not blue) in the vendor CSS, so only the arrow
   and its focus ring need overriding. */
.component .arrow { background: ${INTERACTION} !important; }
.component:hover .arrow::before,
.component:hover .arrow::after { background: ${BRAND} !important; }
.component:focus-visible { outline-color: ${INTERACTION} !important; }

/* The "Passwortkriterien" label span is light-DOM content slotted into that
   button via <slot>; force its color explicitly rather than rely on it
   inheriting the shadow-internal #333 (slotted content's applicable rules
   come from its light-DOM context, not automatically the shadow host's). */
.header ::slotted(span) { color: ${INTERACTION} !important; }

/* sdx-button: theme="primary"/"secondary" hardcode the old brand blue in their
   shadow stylesheet the same way — retheme here so every sdx-button on the
   site (not just the ones inside the dashboard) picks up the new colors. */
button.primary { background: ${PRIMARY} !important; border-color: ${PRIMARY} !important; color: #fff !important; }
button.primary:hover { background: ${INTERACTION} !important; border-color: ${INTERACTION} !important; color: #fff !important; }
button.secondary { background: #fff !important; border-color: ${INTERACTION} !important; color: ${INTERACTION} !important; }
button.secondary:hover { background: ${INTERACTION} !important; border-color: ${INTERACTION} !important; color: #fff !important; }
button.primary:focus-visible,
button.secondary:focus-visible { outline: 3px solid ${INTERACTION} !important; outline-offset: 2px !important; }

/* sdx-button theme="cancel" (the "Details" toggle in CookieBanner) renders as
   text-only blue, not covered by the primary/secondary rules above. */
.cancel { color: ${INTERACTION} !important; }
.cancel:active,
.component:active button.cancel.chip { border-color: rgba(2, 111, 109, 0.15) !important; background-color: rgba(2, 111, 109, 0.15) !important; color: ${BRAND} !important; }
@media (hover: hover) {
  .cancel:hover,
  .component:hover button.cancel.chip { border-color: rgba(2, 111, 109, 0.15) !important; background-color: rgba(2, 111, 109, 0.15) !important; color: ${BRAND} !important; }
}

/* .input.checkbox / .input.radio (bare sdx-input-item, e.g. CookieBanner)
   only has its BASE checked color overridden above via --input-checked-color;
   :hover sets border/background directly (bypassing that custom property),
   so the hover state still hardcoded the old blue. */
.input.checkbox:hover input:not(:disabled).focus+label::before,
.input.checkbox:hover input:not(:disabled):indeterminate+label::before,
.input.radio:hover input:not(:disabled).focus+label::before,
.input.radio:hover input:not(:disabled):indeterminate+label::before { border-color: ${BRAND} !important; }
.input.checkbox:hover input:not(:disabled):checked+label::before,
.input.radio:hover input:not(:disabled):checked+label::before { border-color: ${BRAND} !important; background-color: ${BRAND} !important; }
.input.checkbox:hover input:not(:disabled):indeterminate+label::after,
.input.radio:hover input:not(:disabled):indeterminate+label::after { color: ${BRAND} !important; }
`;

// Colors SDX sets inline via JS (both the hex it writes and the rgb() form
// browsers normalize `el.style.x` reads back to), mapped to their teal
// replacement. Only rewritten when actually present — so a closed dropdown
// with no inline color set is left alone, unlike a blanket CSS rule.
const INLINE_BLUE_TO_TEAL = {};
[
  ["#0445c8", "rgb(4,69,200)", INTERACTION],
  ["#0036a7", "rgb(0,54,167)", BRAND],
  ["#1781e3", "rgb(23,129,227)", INTERACTION], // sdx-select's open-state label color
  ["#4294ff", "rgb(66,148,255)", INTERACTION],
  ["#147df5", "rgb(20,125,245)", INTERACTION],
  ["#0a71e9", "rgb(10,113,233)", INTERACTION],
  ["#5ca3ff", "rgb(92,163,255)", INTERACTION],
  ["#569ff7", "rgb(86,159,247)", INTERACTION],
].forEach(([hex, rgb, teal]) => {
  INLINE_BLUE_TO_TEAL[hex] = teal;
  INLINE_BLUE_TO_TEAL[rgb] = teal;
});

const INLINE_COLOR_PROPS = [
  "color",
  "borderColor",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "backgroundColor",
  "background",
];

function neutralizeInlineStyle(el) {
  const style = el.style;
  if (!style || !style.length) return;
  for (const prop of INLINE_COLOR_PROPS) {
    const raw = style[prop];
    if (!raw) continue;
    const teal = INLINE_BLUE_TO_TEAL[raw.replace(/\s+/g, "").toLowerCase()];
    if (teal && raw !== teal) style[prop] = teal;
  }
}

let sharedSheet = null;

function getSheet() {
  if (!("CSSStyleSheet" in window)) return null;
  try {
    if (!sharedSheet) {
      sharedSheet = new CSSStyleSheet();
      sharedSheet.replaceSync(OVERRIDE_CSS);
    }
    return sharedSheet;
  } catch {
    return null;
  }
}

function applyToRoot(root) {
  const sheet = getSheet();
  if (sheet) {
    try {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      return;
    } catch {
      // fall through to <style> fallback below
    }
  }

  const style = document.createElement("style");
  style.textContent = OVERRIDE_CSS;
  root.appendChild(style);
}

// A MutationObserver on the document does NOT see mutations happening inside
// a shadow root (each is its own isolated subtree), so a nested component
// created *after* we've already scanned its host (e.g. sdx-select toggling
// its internal value box between states) would otherwise never get themed.
// Every shadow root we find gets its own live observer — watching both new
// nodes and inline `style` attribute changes — so late-arriving nested
// sdx-* elements and their dynamic inline colors are always caught.
function watchRoot(root) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        neutralizeInlineStyle(mutation.target);
        continue;
      }
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) visit(node);
      });
    }
  });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style"],
  });
}

// A custom element can sit in the DOM for a moment before Stencil upgrades
// it and attaches its shadow root — if our MutationObserver callback (itself
// a microtask) runs first, `node.shadowRoot` is still null and there'd be no
// further trigger to re-check *that specific node* later. This retries each
// not-yet-upgraded sdx-* node a few times, independent of when it first
// appeared — a one-time delay schedule anchored to app start (the previous
// approach) misses anything mounted after those delays elapse, e.g. a form
// only rendered once the user navigates to it well into the session.
function retryUntilUpgraded(node) {
  if (node.__greenoneRetrying) return;
  node.__greenoneRetrying = true;
  [0, 50, 150, 400, 1000, 2500].forEach((delay) => {
    window.setTimeout(() => {
      if (node.shadowRoot) visit(node);
    }, delay);
  });
}

function visit(node) {
  neutralizeInlineStyle(node);

  if (node.tagName?.toLowerCase().startsWith("sdx-")) {
    const root = node.shadowRoot;
    if (root) {
      if (!root.__greenoneThemed) {
        root.__greenoneThemed = true;
        applyToRoot(root);
        watchRoot(root);
      }
      root.querySelectorAll("*").forEach(visit);
    } else {
      retryUntilUpgraded(node);
    }
  }
  node.querySelectorAll?.("*").forEach(visit);
}

let started = false;

export function startSdxThemeOverride() {
  if (started || typeof document === "undefined") return;
  started = true;

  visit(document.documentElement);
  watchRoot(document.documentElement);
}
